import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";

const MAX_TRIES = 12;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error

  const poll = useCallback(async (sessionId, tries) => {
    if (tries >= MAX_TRIES) { setState("timeout"); return; }
    try {
      const r = await api.get(`/payments/status/${sessionId}`);
      if (r.data.payment_status === "paid") { setState("paid"); return; }
      if (r.data.status === "expired" || r.data.payment_status === "failed") { setState("error"); return; }
      setTimeout(() => poll(sessionId, tries + 1), 2000);
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    poll(sessionId, 0);
  }, [poll]);

  return (
    <div className="min-h-screen grain flex flex-col items-center justify-center px-6" data-testid="payment-success-page">
      <div className="mb-10"><Logo /></div>
      <div className="mfp-card p-10 max-w-md w-full text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-[#FF334B] spin mx-auto mb-5" />
            <h1 className="font-head text-2xl font-bold mb-2">Confirmando tu pago…</h1>
            <p className="text-sm text-[#94A3B8]">Estamos acreditando tus créditos. Un momento.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-5" data-testid="payment-success-icon" />
            <h1 className="font-head text-2xl font-bold mb-2">¡Pago confirmado!</h1>
            <p className="text-sm text-[#94A3B8] mb-6">Tus créditos ya están en tu cuenta. A crear.</p>
            <button data-testid="payment-success-continue" onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold px-6 py-3 rounded-full transition-colors">
              Ir al estudio <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="w-14 h-14 text-[#FF9F2E] mx-auto mb-5" />
            <h1 className="font-head text-2xl font-bold mb-2">
              {state === "timeout" ? "Procesando tu pago" : "No pudimos confirmar el pago"}
            </h1>
            <p className="text-sm text-[#94A3B8] mb-6">
              {state === "timeout"
                ? "El pago puede tardar unos segundos en reflejarse. Revisa tu saldo en Créditos."
                : "Si se te cobró, tus créditos aparecerán en breve. Contáctanos si no es así."}
            </p>
            <button onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF334B] hover:text-[#FF334B] font-semibold px-6 py-3 rounded-full transition-colors">
              Ir al estudio <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
